import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Avatar } from '@/components/ui/Avatar';
import { Modal } from '@/components/ui/Modal';
import { SelectableCard } from '@/components/ui/SelectableCard';
import { Spinner } from '@/components/ui/Spinner';
import { useObjectUrl } from '@/features/organization/useObjectUrl';
import type { OrganizationChoiceResponse } from './useGoogleSignIn';


export type PickerFailure = 'refused' | 'failed';

export interface OrganizationPickerDialogProps {
  open: boolean;
  organizations: OrganizationChoiceResponse[];
  pendingId: string | null;
  failure: PickerFailure | null;
  onSelect: (organizationId: string) => void;
}

/**
 * Shown only when more than one membership comes back.
 *
 * It has no close control on purpose: a session with no organization selected
 * can reach nothing, so dismissing it would leave the app in a state with
 * nothing to render.
 */
export function OrganizationPickerDialog({
  open,
  organizations,
  pendingId,
  failure,
  onSelect,
}: OrganizationPickerDialogProps) {
  const { t } = useTranslation();

  return (
    <Modal open={open} onClose={() => undefined} title={t('auth.picker.title')} size="md">
      <div className="flex flex-col gap-3">
        <p className="text-sm text-neutral-500">{t('auth.picker.description')}</p>

        {failure && (
          <Alert variant="error">
            {t(failure === 'refused' ? 'auth.picker.noAccess' : 'auth.picker.failed')}
          </Alert>
        )}

        <ul className="flex flex-col gap-2">
          {organizations.map((organization) => (
            <li key={organization.organization_id}>
              <OrganizationRow
                organization={organization}
                pending={pendingId === organization.organization_id}
                disabled={pendingId !== null}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </Modal>
  );
}

interface OrganizationRowProps {
  organization: OrganizationChoiceResponse;
  pending: boolean;
  disabled: boolean;
  onSelect: (organizationId: string) => void;
}

function OrganizationRow({ organization, pending, disabled, onSelect }: OrganizationRowProps) {
  const { t } = useTranslation();
  const logoUrl = useObjectUrl(organization.small_logo_object ?? organization.logo_object);

  return (
    <SelectableCard
      disabled={disabled}
      onClick={() => onSelect(organization.organization_id)}
      className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:border-brand-400 hover:bg-neutral-100 disabled:pointer-events-none disabled:opacity-60 dark:hover:bg-neutral-200"
    >
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="h-9 w-9 shrink-0 rounded-md object-contain"
        />
      ) : (
        <Avatar name={organization.name} />
      )}

      <span className="flex min-w-0 flex-col">
        <span className="truncate text-[13px] font-bold text-neutral-900">
          {organization.name}
        </span>
        <span className="flex items-center gap-1.5">
          <Swatch color={organization.primary_color} />
          <Swatch color={organization.secondary_color} />
        </span>
      </span>

      <span className="ml-auto shrink-0 text-xs font-bold text-brand-600">
        {pending ? <Spinner size="sm" /> : t('auth.picker.select')}
      </span>
    </SelectableCard>
  );
}

/** A brand colour is client data, not a token, so it arrives as an inline style. */
function Swatch({ color }: { color: string }) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return null;
  return (
    <span
      aria-hidden
      style={{ backgroundColor: color }}
      className="mt-1 inline-block h-2.5 w-2.5 rounded-full border border-neutral-200"
    />
  );
}
